import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";
import logger from '@/utils/logger';
import { ErrorSource, handleError } from '@/utils/errorHandling';

// Replace DialogTrigger imports with the correct path
import { DialogTrigger } from "@/components/ui/dialog";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class GlobalErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error: error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to an error reporting service
    logger.error("GlobalErrorBoundary caught an error:", error, errorInfo);

    // Log with additional context
    logger.error("React Error Boundary caught an error:", error, {
      module: "GlobalErrorBoundary",
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href
    });

    // Use centralized error handler
    handleError(error, {
      source: ErrorSource.General, // Fix: changed from ErrorSource.ErrorBoundary
      context: "react-error-boundary",
      toastTitle: "Component Error",
      toastDescription: "An error occurred while rendering a component" 
    });

    this.setState({ errorInfo: errorInfo });
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <Dialog open={this.state.hasError} onOpenChange={(open) => {
          if (!open) {
            this.resetErrorBoundary();
          }
        }}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>
                <AlertTriangle className="h-5 w-5 mr-2 inline-block align-middle" />
                Application Error
              </DialogTitle>
              <DialogDescription>
                Oops! Something went wrong. Please try refreshing the page.
                If the problem persists, contact support.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" onClick={this.resetErrorBoundary}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
